/**
 * WhatsAppAdmin.tsx — Admin → Integrations → WhatsApp.
 *
 * One card over one legacy table (`aa_accounts_whatsapp`): the number the
 * practice sends WhatsApp messages from. No credentials live here — the Twilio
 * account is reused from the practice's existing voice/SMS settings — so unlike
 * TreatRxAdmin/AiSettingsAdmin nothing is masked and there is no level-4 gate.
 *
 * Shell copied from AiSettingsAdmin (the closest sibling: single card, no tabs),
 * including its `p-6 space-y-6 max-w-2xl` page root.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the saved number shows read-only
 * with no Add Number, Edit, Disconnect or Test connection (a passing test
 * records the approval server-side, so it writes), the form never opens, and
 * no sentence invites setting the number up. Nothing says why.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { getErrorMessage } from '@/services/apiClient';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  getWhatsAppConnection,
  updateWhatsAppConnection,
  disconnectWhatsApp,
  testWhatsAppConnection,
  type WhatsAppConnectionCheck,
} from '@/services/whatsappAdmin.service';

/**
 * Plain-language result of a connection check. Every failure names what is
 * wrong AND who fixes it; "couldn't check" is never phrased as a failure,
 * because a provider blip must not send someone off to re-register a number
 * that was already fine.
 *
 * Twilio is named on purpose here: the reader has to go and do something in it.
 */
function checkSummary(result: WhatsAppConnectionCheck): { tone: 'ok' | 'warn' | 'bad'; text: string } {
  if (result.credentials.state === 'fail') {
    return {
      tone: 'bad',
      text:
        "We couldn't sign in to your messaging provider, so nothing could be checked. " +
        "Ask your Prospera administrator to confirm this practice's Twilio details.",
    };
  }

  if (result.credentials.state === 'unknown' || result.sender.state === 'unknown') {
    return {
      tone: 'warn',
      text:
        "We couldn't reach WhatsApp to check just now, so this isn't a failure — try again in a " +
        "moment. Your saved settings haven't changed.",
    };
  }

  if (result.sender.state === 'fail') {
    const status = result.sender.status;

    if (!status) {
      return {
        tone: 'bad',
        text:
          "WhatsApp doesn't recognise this number on your Twilio account. It has to be added as a " +
          'WhatsApp sender in Twilio and approved by WhatsApp before you can message patients. ' +
          'Check the number here matches the one you registered.',
      };
    }

    if (['PENDING_VERIFICATION', 'VERIFYING', 'TWILIO_REVIEW'].includes(status)) {
      return {
        tone: 'warn',
        text:
          "This number is registered but still being reviewed by WhatsApp, so it can't send yet. " +
          'Check back once the review finishes — nothing more to do here.',
      };
    }

    return {
      tone: 'warn',
      text:
        "This number is registered but isn't switched on for messaging yet. Finish setting it up " +
        'in Twilio, then test again.',
    };
  }

  if (result.messagingService.state === 'fail') {
    return {
      tone: 'bad',
      text:
        "That messaging service doesn't exist on this Twilio account. Clear the field to send from " +
        'the number instead.',
    };
  }

  if (result.messagingService.state === 'unknown') {
    return {
      tone: 'warn',
      text:
        "The number is live, but we couldn't check the messaging service just now. Try again in a " +
        'moment.',
    };
  }

  const shared = result.credentials.usingPracticeAccount
    ? ''
    : " Note: this number is live on Prospera's shared Twilio account rather than your own.";

  return {
    tone: 'ok',
    text: 'Connected. This number is live on WhatsApp and staff can send from it.' + shared,
  };
}

const QUERY_KEY = ['admin', 'whatsapp-connection'];

export function WhatsAppAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getWhatsAppConnection,
    staleTime: 30_000,
  });

  const [editing, setEditing] = useState(false);
  const [senderNumber, setSenderNumber] = useState('');
  const [messagingServiceSid, setMessagingServiceSid] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isApproved, setIsApproved] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  // This page is reachable whether or not WhatsApp messaging is switched on for
  // the practice, so it has to say which of those it is — otherwise someone
  // saves a number and cannot work out why staff still see no WhatsApp button.
  const { explicit: messagingOn, isLoading: moduleLoading } = useModuleAccess('whatsapp');

  const hasConnection = Boolean(data?.senderNumber || data?.messagingServiceSid);

  // Open straight into the form when there is nothing saved yet, so the first
  // visit is one screen rather than an empty state plus a click.
  useEffect(() => {
    if (data && !hasConnection && !locked) setEditing(true);
  }, [data, hasConnection, locked]);

  function startEditing() {
    setSenderNumber(data?.senderNumber ?? '');
    setMessagingServiceSid(data?.messagingServiceSid ?? '');
    setDisplayName(data?.displayName ?? '');
    setIsApproved(data?.isApproved ?? false);
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setSenderNumber('');
    setMessagingServiceSid('');
    setDisplayName('');
    setIsApproved(false);
  }

  const saveMutation = useMutation({
    mutationFn: updateWhatsAppConnection,
    onSuccess: (res) => {
      toast.success('WhatsApp connection saved.');
      qc.setQueryData(QUERY_KEY, res);
      cancelEditing();
    },
    onError: (err) =>
      toast.error(
        getErrorMessage(
          err,
          "We couldn't save the WhatsApp connection. Try again; if it keeps failing, contact Prospera support.",
        ),
      ),
  });

  const [checkResult, setCheckResult] = useState<WhatsAppConnectionCheck | null>(null);

  const testMutation = useMutation({
    // Send the typed number while editing so a practice can check it before
    // committing; fall back to the saved one otherwise.
    mutationFn: () => testWhatsAppConnection(editing ? senderNumber.trim() || undefined : undefined),
    onSuccess: (res) => {
      setCheckResult(res);
      // A passing check records the approval server-side, so refetch rather
      // than assuming what it wrote.
      if (res.canSend) qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      setCheckResult(null);
      toast.error(getErrorMessage(err, "We couldn't run the check. Try again in a moment."));
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectWhatsApp,
    onSuccess: (res) => {
      toast.success('WhatsApp disconnected.');
      qc.setQueryData(QUERY_KEY, res);
      setConfirmDisconnect(false);
      cancelEditing();
    },
    onError: (err) =>
      toast.error(
        getErrorMessage(err, "We couldn't disconnect WhatsApp. Try again, or contact Prospera support."),
      ),
  });

  function handleSave() {
    if (locked) return;
    const digits = senderNumber.replace(/\D/g, '');
    if (!digits) {
      toast.error('Enter the WhatsApp number for this practice.');
      return;
    }
    if (digits.length < 10) {
      toast.error('Enter a WhatsApp number as 10 digits, or with the country code.');
      return;
    }

    saveMutation.mutate({
      senderNumber: senderNumber.trim(),
      messagingServiceSid: messagingServiceSid.trim(),
      displayName: displayName.trim(),
      isApproved,
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <PageHeader title="WhatsApp" />

      {!moduleLoading && !messagingOn && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          WhatsApp messaging is turned off for this practice, so staff won&rsquo;t see WhatsApp in a
          patient&rsquo;s conversation yet.
          {!locked && (
            <>
              {' '}You can set up your number here now &mdash; ask your Prospera administrator to
              turn WhatsApp on when you&rsquo;re ready to start messaging.
            </>
          )}
        </div>
      )}

      <Card>
        <CardBody>
          <div className="flex items-center gap-3 mb-4">
            <MessageCircle className="h-5 w-5 text-emerald-500" />
            <h3 className="text-base font-semibold text-slate-200">WhatsApp Number</h3>
            {/* "Ready to send" needs BOTH a usable approved sender and
                messaging switched on — the number alone does not send. */}
            {data?.connected && messagingOn ? (
              <Badge variant="success">Ready to send</Badge>
            ) : hasConnection ? (
              <Badge variant="neutral">Not ready yet</Badge>
            ) : null}
          </div>

          {!hasConnection && (locked || !editing) && (
            <p className="text-sm text-slate-400 mb-5">
              No WhatsApp number yet.
              {!locked && (
                <>
                  {' '}Add the number your practice uses for WhatsApp, and staff will be able to
                  send WhatsApp messages from a patient&rsquo;s conversation.
                </>
              )}
            </p>
          )}

          {hasConnection && !data?.isApproved && !locked && (
            <p className="text-sm text-slate-400 mb-5">
              Messages will not send until you confirm below that WhatsApp has approved this
              number for your practice.
            </p>
          )}

          {(locked || !editing) && hasConnection && (
            <div className="space-y-3 mb-5">
              <div>
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                  WhatsApp Number
                </span>
                <p className="text-sm text-slate-200 font-mono mt-0.5">{data?.senderNumber || '—'}</p>
              </div>
              {data?.displayName && (
                <div>
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                    Business Name
                  </span>
                  <p className="text-sm text-slate-200 mt-0.5">{data.displayName}</p>
                </div>
              )}
              {data?.messagingServiceSid && (
                <div>
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                    Messaging Service ID
                  </span>
                  <p className="text-sm text-slate-200 font-mono mt-0.5">{data.messagingServiceSid}</p>
                </div>
              )}
              <div>
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                  Approved by WhatsApp
                </span>
                <p className="text-sm text-slate-200 mt-0.5">{data?.isApproved ? 'Yes' : 'Not yet'}</p>
              </div>
            </div>
          )}

          {/* Locked: no Add/Edit, Test or Disconnect, so the form never opens. */}
          {locked ? null : editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                  WhatsApp Number
                </label>
                <Input
                  value={senderNumber}
                  onChange={(e) => setSenderNumber(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">
                  The number your practice uses for WhatsApp. This is separate from the number you
                  use for text messages.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                  Business Name
                </label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your practice name"
                />
                <p className="text-xs text-slate-500 mt-1">
                  The name patients see on your messages.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                  Messaging Service ID (optional)
                </label>
                <Input
                  value={messagingServiceSid}
                  onChange={(e) => setMessagingServiceSid(e.target.value)}
                  placeholder="Leave blank to send from the number"
                  className="font-mono"
                />
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isApproved}
                  onChange={(e) => setIsApproved(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-sm text-slate-300">
                  WhatsApp has approved this number for my practice.
                  <span className="block text-xs text-slate-500 mt-0.5">
                    Staff cannot send WhatsApp messages until this is checked.
                  </span>
                </span>
              </label>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate()}
                  disabled={testMutation.isPending || !senderNumber.trim()}
                >
                  {testMutation.isPending ? 'Checking…' : 'Test connection'}
                </Button>
                {hasConnection && (
                  <Button variant="secondary" onClick={cancelEditing} disabled={saveMutation.isPending}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button onClick={startEditing}>{hasConnection ? 'Edit' : 'Add Number'}</Button>
              {hasConnection && (
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate()}
                  disabled={testMutation.isPending}
                >
                  {testMutation.isPending ? 'Checking…' : 'Test connection'}
                </Button>
              )}
              {hasConnection && (
                <Button variant="secondary" onClick={() => setConfirmDisconnect(true)}>
                  Disconnect
                </Button>
              )}
            </div>
          )}

          {checkResult && (() => {
            const summary = checkSummary(checkResult);
            const tone =
              summary.tone === 'ok'
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                : summary.tone === 'warn'
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                  : 'border-red-500/40 bg-red-500/10 text-red-200';

            return (
              <div className={`mt-5 rounded-lg border px-4 py-3 text-sm ${tone}`}>
                {summary.text}
              </div>
            );
          })()}
        </CardBody>
      </Card>

      <ConfirmDialog
        isOpen={confirmDisconnect}
        title="Disconnect WhatsApp?"
        message={
          'Staff will no longer be able to send WhatsApp messages from this practice. ' +
          "Past WhatsApp conversations stay in the patient's communication history. " +
          'You can reconnect at any time.'
        }
        confirmLabel="Disconnect"
        onConfirm={() => disconnectMutation.mutateAsync().then(() => undefined)}
        onClose={() => setConfirmDisconnect(false)}
      />
    </div>
  );
}
